import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { initCropper, destroyCropper, getCroppedBlob, applyRotation, CROPPER_PRESETS, bindModalActions, unbindModalActions, lockBodyScroll, unlockBodyScroll } from "../helpers/cropper_helper"
import { isHeic, convertHeicToJpeg } from "../helpers/heic_helper"

export default class extends Controller {
  static targets = ["input", "cameraInput", "previews", "placeholder", "modal", "cropImage", "blobIds"]
  static values = { previewUrl: { type: String, default: "/recipe_imports/preview" } }

  connect() {
    this.entries = [] // { file, blobId, previewUrl }
    this.cropper = null
    // Photos shared to Recidex from the iOS share sheet: the app hands them over
    // with an event, or leaves them on window if it got here before we did.
    this.receiveShared = (event) => {
      delete window.__recidexSharedImages
      this.addShared(event.detail?.images)
    }
    window.addEventListener("recidex:shared-images", this.receiveShared)
    if (window.__recidexSharedImages) {
      const images = window.__recidexSharedImages
      delete window.__recidexSharedImages
      this.addShared(images)
    }
  }

  addShared(images = []) {
    const files = images
      .filter((image) => image?.type?.startsWith("image/") && image.base64)
      .map((image) => new File([ Uint8Array.from(window.atob(image.base64), (c) => c.charCodeAt(0)) ], image.name || "shared.jpg", { type: image.type }))
    if (files.length) this.addFiles(files)
  }

  browse() {
    this.inputTarget.click()
  }

  capture() {
    if (this.hasCameraInputTarget) {
      this.cameraInputTarget.click()
    } else {
      this.inputTarget.click()
    }
  }

  select(event) {
    const source = event?.currentTarget || this.inputTarget
    this.addFiles(Array.from(source.files))
  }

  dragover(event) {
    event.preventDefault()
    this.element.classList.add("ring-warm-400", "bg-warm-50")
  }

  dragleave() {
    this.element.classList.remove("ring-warm-400", "bg-warm-50")
  }

  drop(event) {
    event.preventDefault()
    this.dragleave()
    this.addFiles(Array.from(event.dataTransfer.files).filter(f => f.type.startsWith("image/") || f.type === ""))
  }

  async addFiles(newFiles) {
    for (const file of newFiles) {
      const converted = isHeic(file) ? await convertHeicToJpeg(file) : file
      const index = this.entries.length
      this.entries.push({ file: converted, blobId: null, previewUrl: null, uploading: true })
      this.renderPreviews()
      this.uploadFile(converted, index)
    }
  }

  async uploadFile(file, index) {
    const formData = new FormData()
    formData.append("image", file)

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content },
        body: formData
      })

      if (!response.ok) throw new Error("Upload failed")

      const data = await response.json()
      if (!this.entries?.[index]) return
      this.entries[index].blobId = data.blob_id
      this.entries[index].previewUrl = data.preview_url
      this.entries[index].uploading = false
    } catch {
      if (!this.entries?.[index]) return
      this.entries[index].uploading = false
      this.entries[index].error = true
    }

    if (!this.hasPreviewsTarget) return
    this.renderPreviews()
    this.syncBlobIds()
  }

  removeFile(index) {
    this.entries.splice(index, 1)
    this.renderPreviews()
    this.syncBlobIds()
  }

  openCrop(index) {
    this.cropIndex = index
    const entry = this.entries[index]

    this.modalWrapper = this.modalTarget
    this.modalEl = this.modalWrapper.querySelector("[data-crop-modal]") || this.modalWrapper
    this.cropImage = this.modalEl.querySelector(".js-crop-image")

    const loadImage = (src) => {
      document.body.appendChild(this.modalWrapper)
      this.modalEl.classList.remove("hidden")
      lockBodyScroll()
      this.handlers = bindModalActions(this.modalEl, {
        close: () => this.closeCrop(),
        apply: () => this.applyCrop(),
        rotateLeft: () => this.rotateLeft(),
        rotateRight: () => this.rotateRight(),
        zoomIn: () => this.zoomIn(),
        zoomOut: () => this.zoomOut()
      })

      this.cropImage.onload = () => {
        this.cropper = destroyCropper(this.cropper)
        this.cropper = initCropper(this.cropImage, CROPPER_PRESETS.scan)
      }
      this.cropImage.src = src
    }

    if (entry.previewUrl) {
      loadImage(entry.previewUrl)
    } else {
      const reader = new FileReader()
      reader.onload = (e) => loadImage(e.target.result)
      reader.readAsDataURL(entry.file)
    }
  }

  closeCrop() {
    this.cropper = destroyCropper(this.cropper)
    this.handlers = unbindModalActions(this.modalEl, this.handlers)
    this.modalEl.classList.add("hidden")
    this.element.appendChild(this.modalWrapper)
    unlockBodyScroll()
  }

  async applyCrop() {
    if (!this.cropper) return

    const blob = await getCroppedBlob(this.cropper)
    if (!blob) return

    const original = this.entries[this.cropIndex].file
    const name = original.name.replace(/\.[^.]+$/, "") + "_cropped.jpg"
    const croppedFile = new File([blob], name, { type: "image/jpeg" })

    const index = this.cropIndex
    this.entries[index].file = croppedFile
    this.entries[index].uploading = true
    this.entries[index].previewUrl = null
    this.entries[index].blobId = null

    this.renderPreviews()
    this.closeCrop()
    this.uploadFile(croppedFile, index)
  }

  zoomIn() { if (this.cropper) this.cropper.zoom(0.1) }
  zoomOut() { if (this.cropper) this.cropper.zoom(-0.1) }

  async rotateLeft() {
    if (!this.cropper) return
    this.cropper = await applyRotation(this.cropper, this.cropImage, -90, "scan")
  }

  async rotateRight() {
    if (!this.cropper) return
    this.cropper = await applyRotation(this.cropper, this.cropImage, 90, "scan")
  }

  // private

  syncBlobIds() {
    if (!this.hasBlobIdsTarget) return
    this.blobIdsTarget.innerHTML = ""
    this.entries.forEach(entry => {
      if (entry.blobId) {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "blob_ids[]"
        input.value = entry.blobId
        this.blobIdsTarget.appendChild(input)
      }
    })
  }

  renderPreviews() {
    if (this.sortable) this.sortable.destroy()
    this.previewsTarget.innerHTML = ""

    if (this.entries.length === 0) {
      if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove("hidden")
      return
    }

    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add("hidden")

    this.entries.forEach((entry, index) => {
      const card = document.createElement("div")
      card.className = "relative group"
      card.dataset.sortIndex = index

      const thumb = document.createElement("div")
      thumb.className = "h-48 rounded-xl overflow-hidden ring-1 ring-warm-100 dark:ring-gray-600 bg-gray-100 dark:bg-gray-700 flex items-center justify-center cursor-grab active:cursor-grabbing"

      if (entry.uploading) {
        thumb.innerHTML = `
          <svg class="animate-spin h-5 w-5 text-warm-400" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
        `
      } else if (entry.previewUrl) {
        const img = document.createElement("img")
        img.src = entry.previewUrl
        img.alt = entry.file.name
        img.className = "max-h-full max-w-full object-contain pointer-events-none"
        thumb.appendChild(img)
      } else {
        thumb.innerHTML = '<div class="flex items-center justify-center h-full text-xs text-red-400">Failed</div>'
      }

      card.appendChild(thumb)

      // Drag handle
      if (this.entries.length > 1) {
        const handle = document.createElement("div")
        handle.className = "absolute top-2 left-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm cursor-grab active:cursor-grabbing"
        handle.innerHTML = '<svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" /></svg>'
        card.appendChild(handle)
      }

      if (!entry.uploading) {
        const actions = document.createElement("div")
        actions.className = "absolute bottom-2 right-2 flex items-center gap-1.5"

        const cropBtn = document.createElement("button")
        cropBtn.type = "button"
        cropBtn.className = "flex items-center justify-center h-8 w-8 rounded-full bg-black/60 text-white hover:bg-black/80 backdrop-blur-sm transition-all shadow-sm"
        cropBtn.title = "Crop & Rotate"
        cropBtn.innerHTML = '<svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5" /></svg>'
        cropBtn.addEventListener("click", () => this.openCrop(index))

        const removeBtn = document.createElement("button")
        removeBtn.type = "button"
        removeBtn.className = "flex items-center justify-center h-8 w-8 rounded-full bg-black/60 text-red-300 hover:bg-red-500 hover:text-white backdrop-blur-sm transition-all shadow-sm"
        removeBtn.title = "Remove"
        removeBtn.innerHTML = '<svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>'
        removeBtn.addEventListener("click", () => this.removeFile(index))

        actions.appendChild(cropBtn)
        actions.appendChild(removeBtn)
        card.appendChild(actions)
      }

      const label = document.createElement("p")
      label.className = "text-xs text-gray-400 truncate text-center mt-1"
      label.textContent = this.entries.length > 1 ? `Page ${index + 1}` : entry.file.name.length > 12 ? entry.file.name.slice(0, 10) + "..." : entry.file.name
      card.appendChild(label)

      this.previewsTarget.appendChild(card)
    })

    if (this.entries.length > 1) {
      this.sortable = Sortable.create(this.previewsTarget, {
        animation: 200,
        ghostClass: "opacity-30",
        onEnd: (evt) => this.reorder(evt.oldIndex, evt.newIndex)
      })
    }
  }

  reorder(oldIndex, newIndex) {
    const [ entry ] = this.entries.splice(oldIndex, 1)
    this.entries.splice(newIndex, 0, entry)
    this.syncBlobIds()
    this.renderPreviews()
  }

  disconnect() {
    window.removeEventListener("recidex:shared-images", this.receiveShared)
    if (this.sortable) this.sortable.destroy()
    this.cropper = destroyCropper(this.cropper)
    if (this.modalWrapper?.parentElement === document.body) {
      this.handlers = unbindModalActions(this.modalEl, this.handlers)
      this.element.appendChild(this.modalWrapper)
    }
    unlockBodyScroll()
  }
}
